/*
 * Website Avia - the airport picker's tree: Country (flag) -> City -> City - Airport.
 * QA doc: docs features/website/airport-picker/airport-picker.md (R-1, R-4, R-5, R-7).
 *
 * GetAirportTree answers country -> city -> airport nodes (`value`: ISO code / city UID / IATA code). The picker needs more
 * per row than the API sends: the country's ISO code on every row (the flag), the counts of the country and the city, the
 * city of an airport (the field text), and where a guide line ends. The first three come from `decorateAirportTree`; the
 * guide lines from `airportGuideLines`, over the list as SHOWN (a search shows a part of the tree, so the last city shown
 * of a country is not always its last city).
 */
import { AirportTreeItem } from '@appTypes/redefined-types';

export type AirportNodeType = 'country' | 'city' | 'airport';

/** What a row of the picker shows besides the API node; client-only, never sent back. */
export interface AirportNodeView {
  type: AirportNodeType;
  /** ISO 3166-1 alpha-2 of the node's country, upper case (the country node's `value`). */
  countryCode: string;
  countryName: string;
  /** The city of a city or airport row ('' on a country row). */
  cityName: string;
  /** A country's cities in the whole catalogue (0 on other rows). */
  cityCount: number;
  /** A country's / a city's airports in the whole catalogue (1 on an airport row). */
  airportCount: number;
}

export type AirportTreeNode = AirportTreeItem & {
  _view?: AirportNodeView;
  children?: AirportTreeNode[] | null;
  /** Set by the selector when it flattens the tree (`convertToFlattenTree`). */
  _parentKey?: string;
};

interface CatalogueCounts {
  cities: Map<string, number>;
  airports: Map<string, number>;
}

const countKey = (type: AirportNodeType, value?: string | null) => `${type}:${value ?? ''}`;

/** Counts of the whole catalogue by country / city value, so a search answer can show the catalogue's numbers. */
const catalogueCounts = (tree?: AirportTreeItem[] | null): CatalogueCounts => {
  const cities = new Map<string, number>();
  const airports = new Map<string, number>();
  for (const country of tree ?? []) {
    const countryCities = country.children ?? [];
    cities.set(countKey('country', country.value), countryCities.length);
    let countryAirports = 0;
    for (const city of countryCities) {
      const cityAirports = city.children?.length ?? 0;
      airports.set(countKey('city', city.value), cityAirports);
      countryAirports += cityAirports;
    }
    airports.set(countKey('country', country.value), countryAirports);
  }
  return { cities, airports };
};

/**
 * Adds `_view` to every node (new objects; the store's tree is frozen). `catalogue` is the whole tree: when `nodes` is a
 * search answer, the counts still describe the catalogue (R-1: "4 города · 5 аэропортов" of the country, not of the match).
 */
export const decorateAirportTree = (
  nodes: AirportTreeItem[] | null | undefined,
  catalogue?: AirportTreeItem[] | null
): AirportTreeNode[] => {
  const counts = catalogueCounts(catalogue ?? nodes);
  return (nodes ?? []).map((country) => {
    const countryCode = (country.value ?? '').toUpperCase();
    const countryName = country.label ?? '';
    const cities = country.children ?? [];
    return {
      ...country,
      _view: {
        type: 'country',
        countryCode,
        countryName,
        cityName: '',
        cityCount: counts.cities.get(countKey('country', country.value)) ?? cities.length,
        airportCount: counts.airports.get(countKey('country', country.value)) ?? 0
      },
      children: cities.map((city) => {
        const cityName = city.label ?? '';
        const airports = city.children ?? [];
        return {
          ...city,
          _view: {
            type: 'city',
            countryCode,
            countryName,
            cityName,
            cityCount: 0,
            airportCount: counts.airports.get(countKey('city', city.value)) ?? airports.length
          },
          children: airports.map((airport) => ({
            ...airport,
            _view: { type: 'airport', countryCode, countryName, cityName, cityCount: 0, airportCount: 1 }
          }))
        };
      })
    };
  });
};

/** Where the guide lines of a row stop (R-1), computed over the rows as shown. */
export interface AirportGuideLines {
  /** City row: the last city shown under its country - the country's line ends at it. */
  isLastCity: boolean;
  /** Airport row: the last airport shown under its city - the city's line ends at it. */
  isLastAirport: boolean;
  /** Airport row: its city is the last city shown - no country line passes this row. */
  cityIsLast: boolean;
  /** City row: an airport row follows it (a search of one or two letters can show a city without its airports). */
  hasAirportBelow: boolean;
}

const NO_LINES: AirportGuideLines = {
  isLastCity: false,
  isLastAirport: false,
  cityIsLast: false,
  hasAirportBelow: false
};
const guideLinesCache = new WeakMap<readonly AirportTreeNode[], AirportGuideLines[]>();

const rowType = (node?: AirportTreeNode): AirportNodeType | undefined => node?._view?.type;

/** One backward pass per list (cached per list instance): a row's lines depend only on the rows after it. */
const computeGuideLines = (rows: readonly AirportTreeNode[]): AirportGuideLines[] => {
  const lines: AirportGuideLines[] = new Array(rows.length);
  let nextNonAirport: AirportNodeType | undefined; // type of the first non-airport row after i
  let nextIsAirport = false; // the row right after i is an airport
  let cityAfterInCountry = false; // a city row follows before the next country row
  for (let i = rows.length - 1; i >= 0; i--) {
    const type = rowType(rows[i]);
    if (type === 'airport') {
      lines[i] = {
        isLastCity: false,
        isLastAirport: !nextIsAirport,
        cityIsLast: nextNonAirport !== 'city',
        hasAirportBelow: false
      };
    } else if (type === 'city') {
      lines[i] = {
        isLastCity: !cityAfterInCountry,
        isLastAirport: false,
        cityIsLast: false,
        hasAirportBelow: nextIsAirport
      };
    } else lines[i] = NO_LINES;

    nextIsAirport = type === 'airport';
    if (type !== 'airport') nextNonAirport = type;
    if (type === 'city') cityAfterInCountry = true;
    else if (type === 'country') cityAfterInCountry = false;
  }
  return lines;
};

export const airportGuideLines = (
  rows: readonly AirportTreeNode[] | null | undefined,
  index: number
): AirportGuideLines => {
  if (!rows?.length || index < 0 || index >= rows.length) return NO_LINES;
  let lines = guideLinesCache.get(rows);
  if (!lines) {
    lines = computeGuideLines(rows);
    guideLinesCache.set(rows, lines);
  }
  return lines[index] ?? NO_LINES;
};

/** The country row a row belongs to among the rows shown (R-7, the pinned country); null when there is none. */
export const airportCountryRow = (
  row: AirportTreeNode | null | undefined,
  rows: readonly AirportTreeNode[] | null | undefined
): AirportTreeNode | null => {
  const code = row?._view?.countryCode;
  if (!code || !rows) return null;
  if (rowType(row) === 'country') return row;
  return rows.find((r) => rowType(r) === 'country' && r._view?.countryCode === code) ?? null;
};

/** Translation key of the field text (R-4); the English text is in en-US.json, the Russian in ru-RU.json. */
export const AIRPORT_ALL_AIRPORTS_KEY = 'AIRPORT_PICKER_ALL_AIRPORTS';

/**
 * R-4: the field text after a pick - `<City> · <IATA>` for an airport or a one-airport city, `<City> · все аэропорты` for a
 * city with several, only `<Country>` for a country (user, 08.10.2026: "whole country" is not needed in the field). `translate` turns the keys above into the current language.
 */
export const airportFieldText = (
  node: AirportTreeNode | null | undefined,
  translate: (key: string) => string
): string => {
  const view = node?._view;
  if (!node || !view) return node?.label?.trim() ?? '';
  if (view.type === 'country') return view.countryName;
  if (view.type === 'airport') return `${view.cityName} · ${node.value ?? ''}`;
  const only = node.children?.length === 1 && view.airportCount === 1 ? node.children[0] : null;
  return only ? `${view.cityName} · ${only.value ?? ''}` : `${view.cityName} · ${translate(AIRPORT_ALL_AIRPORTS_KEY)}`;
};

/**
 * R-5: the row the keyboard highlight goes to when a search answer arrives: an exact IATA code -> that airport; else the
 * first city whose name starts with the text, then a country, then an airport name, then any name containing it; else the
 * first row. -1 for an empty list.
 */
export const airportPreferredIndex = (
  rows: readonly AirportTreeNode[] | null | undefined,
  query?: string | null
): number => {
  if (!rows?.length) return -1;
  const q = query?.trim().toLowerCase() ?? '';
  if (!q) return 0;
  const score = (row: AirportTreeNode): number => {
    const type = rowType(row);
    const name = (row.label ?? '').toLowerCase();
    if (type === 'airport' && (row.value ?? '').toLowerCase() === q) return 0;
    if (type === 'city' && name.startsWith(q)) return 1;
    if (type === 'country' && name.startsWith(q)) return 2;
    if (type === 'airport' && name.startsWith(q)) return 3;
    if (type === 'city' && name.includes(q)) return 4;
    if (type === 'airport' && (name.includes(q) || (row.value ?? '').toLowerCase().startsWith(q))) return 5;
    if (type === 'country' && name.includes(q)) return 6;
    return 99;
  };
  let best = 0;
  let bestScore = 99;
  rows.forEach((row, index) => {
    const s = score(row);
    if (s < bestScore) {
      bestScore = s;
      best = index;
    }
  });
  return best;
};

/** A text split around the first case-insensitive match of `query`, for highlighting without innerHTML (R-5). */
export interface HighlightPart {
  text: string;
  match: boolean;
}
export const highlightParts = (text: string | null | undefined, query?: string | null): HighlightPart[] => {
  const value = text ?? '';
  const q = query?.trim() ?? '';
  const at = q ? value.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (at < 0) return [{ text: value, match: false }];
  return [
    { text: value.slice(0, at), match: false },
    { text: value.slice(at, at + q.length), match: true },
    { text: value.slice(at + q.length), match: false }
  ].filter((part) => part.text.length > 0);
};
